import { SubjectRawRule } from '@casl/ability';
import { PackRule } from '@casl/ability/extra';
import { Action } from '../AppAbility';
import {
	ContactProfilePaymentStatusCode,
	ContactProfileStatusCode,
} from './contact';
import { MultiLangText, MultiLangFullText } from './locale';
import { UserAccount } from './user';
export * from './locale';
export * from './job';
export * from './event';
export * from './user';
export * from './contact';
export * from './volunteer';
export * from './program';
export * from './dict-field';

type Dictionary<T> = { [key: string]: T };

export interface Breadcrumb {
	name: string;
	i18nKey?: string;
	text?: string;
	to?: string;
}

export interface NestedFieldValue {
	[fieldKey: string]: FieldValue;
}

export type SimpleFieldValue =
	| string
	| number
	| boolean
	| File
	| Array<string | number | boolean>;

export type FieldValue =
	| SimpleFieldValue
	| Array<NestedFieldValue>
	| NestedFieldValue;

export interface FormData {
	[fieldKey: string]: FieldValue;
}

export interface DynamicFormSettings {
	header?: MultiLangText;
	footer?: MultiLangText;
	extraFields: ExtraFields;
}

export enum TemplateStatusCode {
	draft = 'draft',
	pending = 'pending',
	approved = 'approved',
	published = 'published',
}

export type TemplateStatusChangeActionCode =
	| 'submit'
	| 'approve'
	| 'unapprove'
	| 'publish'
	| 'unpublish'
	| 'cancel';

export interface TemplateStatusChange {
	id: number;
	templateId: number;
	action: TemplateStatusChangeActionCode;
	createdAt: Date;
}

export interface Template {
	id?: number;
	centerId?: number;
	contactType: ContactTypeCode;
	name: MultiLangText;
	instructions: MultiLangText;
	formType: FormTypeCode;
	settings: DynamicFormSettings;
	status: TemplateStatusCode;
	extraSetting?: ContactExtraSetting;
	mainNotificationEmail: string;
	extraNotificationEmails: string[];
	statusChanges?: TemplateStatusChange[];
	createdAt?: Date;
}

export interface FormTypeItem {
	name: MultiLangText;
	formType: string;
	contactType: ContactTypeCode;
	settings: DynamicFormSettings;
}

export type FormFields = Dictionary<DictionaryFieldProperties>;

export interface ExtraFields {
	fields: FormFields;
	displaySettings: FormSection[];
}

export interface ContactExtraSetting {
	planIds: string[];
	autoApprove: boolean;
}

export interface Form {
	id: number;
	publicId: string;
	templateId: number;
	settings: DynamicFormSettings;
	extraSetting: ContactExtraSetting; // TODO: | EventExtraSetting
	mainNotificationEmail: string;
	extraNotificationEmails: string[];
	template: Template;
	active: boolean;
	centerId: number;
	updatedAt?: Date;
}

export interface SubmitForm {
	id: string;
	templateId: number;
	data: FormData;
	formId: number;
	form: Form;
}

export interface FormSection {
	id?: string;
	fields: string[];
	groupName: MultiLangText;
}

export interface Option {
	name: keyof Properties;
	check: boolean;
	values: ValueType;
	hidden?: boolean;
}
export type ValueType =
	| MultiLangText
	| (MCMSELOption | string)[]
	| number
	| string
	| boolean
	| Dependency[]
	| null
	| string[];

export interface Properties {
	desc?: MultiLangText;
	hint?: MultiLangText;
	placeholder?: MultiLangText;
	values?: (MCMSELOption | string)[];
	min?: number;
	max?: number;
	type?: BasicFieldType;
	mandatory?: boolean;
	default?: ValueType; //TODO: default type
	prerequisite?: string;
	elements?: string[];
	style?: string;
	dependencies?: Dependency[];
	mapId?: string;
	allowOthers?: boolean;
	externalValues?: boolean;
	decimals?: number;
	fullWidth?: boolean;
	readOnly?: boolean;
	addButton?: { desc: MultiLangText };
	customValidations?: { [validationRuleName: string]: any };
	domainValidation?: string[];
	link?: { text: MultiLangText; url?: MultiLangText };
	acceptedTypes?: string[];
	// add_more?: boolean;
	// no_delete?: boolean;
	// values_source?: string;
	// values_source_body?: {};
	// autofill?: {};
	// autofill_display?: {};
	// crop?: {};
	digits?: number;
	username?: string;
	lowerCaseOnly?: boolean;
	enableConfirmEmail?: boolean;
	cssStyle?: any;
	emptyMessage?: MultiLangText;
}

export interface DictionaryFieldProperties extends Properties {
	mapId: string; // required
	children?: DictionaryFieldProperties[];
}

export interface Dependency {
	id?: string;
	rule?: { [ruleKey: string]: string | string[] };
	fields?: string[];
}

export interface MCMSELOption {
	k: string;
	v: MultiLangText | string;
}

export interface FieldPresetBase {
	displayKeys: string[];
	fields: FormFields;
}

export interface PresetField extends FieldPresetBase {
	id: number;
}

export interface CustomField extends FieldPresetBase {
	id?: number;
	name?: string;
}

export interface ContactPresetField extends FieldPresetBase {
	names: MultiLangText;
}

export interface FormProps {
	field: Properties;
	fieldKey: string;
	section?: FormSection;
	sectionIndex?: number;
	parentFieldKey?: string;
	arrayParentFieldKey?: string;
	selectionItem?: (MCMSELOption | string)[];
}

export enum BasicFieldType {
	TEXT = 'TEXT',
	PASSWORD = 'PASSWORD',
	INTEGER = 'INTEGER',
	NUMBER = 'NUMBER',
	CURRENCY = 'CURRENCY', //new, used by Prgram form
	URL = 'URL',
	EMAIL = 'EMAIL',
	CHECKBOX = 'CHECKBOX',
	DATE = 'DATE',
	TIME = 'TIME',
	DATETIME = 'DATETIME',
	MC = 'MC',
	MSEL = 'MSEL',
	TAGS = 'TAGS',
	MTEXT = 'MTEXT',
	FILE = 'FILE',
	IMAGE = 'IMAGE',
	GROUP = 'GROUP',
	STATICTEXT = 'STATICTEXT',
	ARRAY = 'ARRAY',
	STATICLINK = 'STATICLINK',
	REPORTINCOMEITEM2 = 'REPORTINCOMEITEM2',
	REPORTEXPENSEITEM2 = 'REPORTEXPENSEITEM2',
	REPORTREIMBURSEITEM2 = 'REPORTREIMBURSEITEM2',
}
export enum DateType {
	singleDay = 'singleDay',
	period = 'period',
	tbc = 'tbc',
}

export interface FieldOption {
	key: string;
	desc: MultiLangText;
}
export interface FieldConfig {
	key: string;
	desc: MultiLangText;
	type: BasicFieldType;
	mandatory?: boolean;
	options?: FieldOption[];
	protected?: boolean;
	isNew?: boolean;
}

export interface EmailItem {
	email: string;
}

export type EmailItems = Dictionary<EmailItem>;

export interface PhoneItem {
	phone: string;
}

export type PhoneItems = Dictionary<PhoneItem>;

export interface AddressItem {
	addressLine: MultiLangText;
	mapLatLong?: string;
	regionCode?: string;
	districtCode?: string;
	subDistrictCode?: string;
}

export interface PlanItem {
	type: string;
	expiryDate: string;
	subscriptionDate: string;
}

export enum NgoRegistrationType {
	taxExempt = 'taxExempt',
	societies = 'societies',
	limitByGuarantee = 'limitByGuarantee',
	limitByShares = 'limitByShares',
}

export interface NgoProfile {
	legalEntityName: MultiLangText;
	establishYear?: string;
	description: MultiLangText;
	missionAndVision: MultiLangText;
	tagline: MultiLangText;
	registrationType?: NgoRegistrationType;
	registrationNumber?: string;
	organisationType?: string;
	religiousBackground: MultiLangText;
	cause?: string[];
	recipient?: string[];
	sdgoal?: string[];
	websiteUrl?: string;
}

export enum TellUsMoreJobRole {
	administrativeSupport = 'administrativeSupport',
	careTaking = 'careTaking',
	cleaning = 'cleaning',
	designing = 'designing',
	drivingOrDelivery = 'drivingOrDelivery',
	eventAssistance = 'eventAssistance',
	fundRaising = 'fundRaising',
	hotlineSupport = 'hotlineSupport',
	itAssistance = 'itAssistance',
	marketingOrAdvocacy = 'marketingOrAdvocacy',
	mentoring = 'mentoring',
	microVolunteering = 'microVolunteering',
	packing = 'packing',
	performing = 'performing',
	photographyAndVideoEdit = 'photographyAndVideoEdit',
	professionalConsulting = 'professionalConsulting',
	projectPlanning = 'projectPlanning',
	repairing = 'repairing',
	research = 'research',
	translationAndEditorial = 'translationAndEditorial',
	tutoringAndTeaching = 'tutoringAndTeaching',
	visit = 'visit',
	volunteerInternship = 'volunteerInternship',
	volunteerTraining = 'volunteerTraining',
	other = 'other',
}

export interface Center {
	id?: number;
	code: string;
	name: MultiLangText;
	emails: EmailItems;
	phones: PhoneItems;
	isVirtual: boolean;
	addressLine: MultiLangText;
	mapLatLong?: string;
	regionCode?: string;
	districtCode?: string;
	subDistrictCode?: string;
	logoUrl: string;
	imageUrl: string;
	ngo?: Ngo;
	ngoId: number;
	missionAndVision: MultiLangText;
	cause?: string[];
	recipient?: string[];
	sdgoal?: string[];
	displayOnWebAndApp: boolean;
	bannerUrl: string;
}

export enum NgoStatus {
	inProgress = 'inProgress',
	completed = 'completed',
	verified = 'verified',
	expired = 'expired',
	rejected = 'rejected',
	suspended = 'suspended',
	pending = 'pending',
}

export enum NgoApprovalStatus {
	pending = 'pending',
	approved = 'approved',
	rejected = 'rejected',
	suspended = 'suspended',
}

export enum NgoOnboardingStatus {
	verified = 'verified',
	expired = 'expired',
	pending = 'pending',
	completed = 'completed',
	inProgress = 'inProgress',
}

export interface Ngo {
	id?: number;
	code: string;
	name: MultiLangText;
	mainContact: MainContactUser;
	ngoProfile: NgoProfile;
	logoUrl: string;
	imageUrl: string;
	category?: NgoCategory;
	status: NgoStatus;
	approvalStatus?: NgoApprovalStatus;
	previousApprovalStatus?: NgoApprovalStatus;
	approvalReason?: string;
	onboardingStatus?: NgoOnboardingStatus;
	subscribedPlans?: PlanItem[];
	consents: {
		[consentKey: string]: boolean;
	};
	centers?: Center[];
	dictionaries?: ContactDictionary[];
	signupData?: { [k: string]: any };
	onboardingData?: NgoOnboardingData;
	mainAccountId?: string;
	remarks?: string;
	createdAt?: string;
	updatedAt?: string;
	lastModifiedInfo?: object;
	portalBrandLogoUrl?: string;
}

export interface NgoOnboardingDataStatus {
	additionalInfoFinishedCount?: number;
	additionalInfoTotalCount?: number;
	canSubmit?: boolean;

	profileEditCompleted?: boolean;
	unitEditCompleted?: boolean;
	buildYourProfileStepSubmitted?: boolean;
	
	tellUsMoreCompleted?: boolean;
	additionalInformationSubmitted?: boolean;
}

export interface NgoOnboardingAdditionalInformation {
	consentJC: boolean;
	howDidYouKnowSocialCareer: string[];
	howDidYouKnowSocialCareerOthersText?: string;
	conductsBackgroundChecks: string;
	conductsBackgroundChecksYesText?: string;
	hasInsuranceForOrganisation: string[];
	hasInsuranceForVolunteer: string;
	firstAidStaffCount: string;
	checkSCRC: string;
	riskManagementInPlace: string;
}

export interface NgoOnboardingTellUsMore {
	jobRoleRecruiting: TellUsMoreJobRole[];
	jobRoleRecruitingOtherText: string;
	acceptEnglishVolunteer: string;
	acceptEnglishVolunteerNoReason: string;
	acceptYoungVolunteer: string;
	acceptYoungVolunteerNoReason: string;
}

export interface NgoOnboardingData {
	status?: NgoOnboardingDataStatus;
	additionalInformation?: NgoOnboardingAdditionalInformation;
	tellUsMore?: NgoOnboardingTellUsMore;
}

export interface NgoUpdateDto {
	code?: string;
	name?: MultiLangText;
	// mainContact: MainContactItem;
	ngoProfile?: NgoProfile;
	logoUrl?: string;
	imageUrl?: string;
	// category?: NgoCategory;
	// status?: NgoStatus;
	// subscribedPlans?: SubscribedPlans;
	// consents?: NgoConsents;
	onboardingData?: NgoOnboardingData;
}

export interface CodeValue {
	code: string;
	name: MultiLangFullText;
	deletedAt?: Date;
}

export type Constant = CodeValue & {
	id: number;
	position?: number;
	deletedAt: Date;
	lastModifiedInfo?: LastModifiedInfo;
};

export class AppBannerDto {
	position?: number;
	imageUrl?: MultiLangText;
	centerId?: number;
	destinationType?: DestinationType | null;
	destinationUrl?: MultiLangText;
	eventId?: string | null;
	jobId?: string | null;
}

export enum DestinationType {
	web = 'web',
	app = 'app',
}

export interface AppBanner {
	id?: string;
	position?: number;
	imageUrl: MultiLangText;
	center?: Center;
	centerId?: number;
	destinationType?: DestinationType | null;
	destinationUrl?: MultiLangText;
	eventId?: string | null;
	jobId?: string | null;
	createdAt?: Date;
	updatedAt?: Date;
}

export type Cause = Constant;

export type Recipient = Constant;

export type Sdgoal = Constant;

export type ActivityTheme = Constant;

export enum RegistrationType {
	taxExempt = 'taxExempt',
	societies = 'societies',
	limitByGuarantee = 'limitByGuarantee',
	limitByShares = 'limitByShares',
}

export enum NgoCategory {
	ngo = 'ngo',
	se = 'se',
	school = 'school',
	university = 'university',
	corporate = 'corporate',
}

export interface MainContactUser {
	name: string;
	jobTitle: string;
	phone: string;
	email: string;
}

export interface Country extends CodeValue {
	id: number;
	code2: string;
}

export interface HkRegion extends CodeValue {
	id: number;
}

export interface HkDistrict extends CodeValue {
	id: number;
	region: HkRegion;
}

export interface ConnectInvite {
	id: string;
	code: string;
	link: string;
	email?: string;
}

export class ContactData {
	internalFlag?: string;
	[mapId: string]: any;
}

export interface Contact {
	id: number;
	avatarPath?: string;
	owners?: Owner[];
	ownerAllCenters: boolean;
	ownerCodes: string[];
	fullNameChi?: string;
	firstNameEng?: string;
	lastNameEng?: string;
	appUserId?: string;
	appUserConnected?: boolean;
	connectInvite?: ConnectInvite;
	emails?: ContactEmailItem[];
	phones?: ContactPhoneItem[];
	addresses?: ContactAddressItem[];
	gender?: string;
	dateOfBirth?: string;
	age?: number;
	jobTitle?: string;
	companyName?: string;
	marital?: string;
	countryId?: number;
	country: Country;
	districtId?: number;
	district: HkDistrict;
	regionId?: number;
	region: HkRegion;
	contactProfiles?: ContactProfile[];
	qrCodes?: QRCode[];
	data?: ContactData;
	createdAt?: Date;
	updatedAt?: Date;
	deletedAt?: Date | null;
}

export interface Partner {
	id: string;
	ngoId: number;
	ngo: Ngo;
	partnerNgoId: number;
	partnerNgo: Ngo;
	partnerExtOrgId: string;
	partnerExtOrg: ExternalOrganisation;
	startedAt: Date;
}

export interface PartnerInterface {
	name: MultiLangText;
	category: NgoCategory;
	cause?: string[];
	recipient?: string[];
	sdgoal?: string[];
	deletedAt?: Date;
}
export interface AddPartner extends PartnerInterface {
	scNgoId?: number;
	extOrgId?: string;
	isExternal: boolean;
}

export interface ViewPartner extends PartnerInterface {
	id: string;
	partnerNgoId?: number;
	partnerExtOrgId?: string;
	centers?: Partial<Center>[];
	isExternal: boolean;
	startedAt: Date;
}

export interface ExternalOrganisation extends PartnerInterface {
	id: string;
}

export interface ExtOrgUpdateDto {
	id?: string;
	name?: MultiLangText;
	category?: NgoCategory;
	cause?: string[];
	recipient?: string[];
	sdgoal?: string[];
}

export interface Owner {
	center: Center;
}

export function isKeyOfContact(key: string): key is keyof Contact {
	return [
		'id',
		'centerId',
		'center',
		'owners',
		'ownerAllCenters',
		'ownerCodes',
		'fullNameChi',
		'firstNameEng',
		'lastNameEng',
		'data.fullNameEng',
		'emails',
		'phones',
		'addresses',
		'gender',
		'dateOfBirth',
		'age',
		'jobTitle',
		'companyName',
		'marital',
		'countryId',
		'country',
		'districtId',
		'district',
		'regionId',
		'region',
		'contactProfiles',
		'data',
		'updatedAt',
	].includes(key);
}

export function isFieldOfContact(key?: string): boolean {
	if (!key) return false;
	return (
		(isKeyOfContact(key) &&
			![
				/**
				 * key of contact but not field
				 */
				'id',
				'centerId',
				'center',
				'contactProfiles',
				'data',
			].includes(key)) ||
		/**
		 * special keys like emails.general
		 */
		(key.split('.').length === 2 &&
			['emails', 'phones', 'addresses'].includes(key.split('.')[0]))
	);
}

export interface ContactDto {
	id?: number;
	ngoId?: number;
	centerIds?: number[] | null;
	owners?: Owner[];
	ownerAllCenters?: boolean;
	ownerCodes?: string[];
	avatarPath?: string;
	fullNameChi?: string;
	firstNameEng?: string;
	lastNameEng?: string;
	appUserId?: string;
	appUserConnected?: boolean;
	connectInvite?: ConnectInvite;
	emails?: ContactEmailItem[];
	phones?: ContactPhoneItem[];
	addresses?: ContactAddressItem[];
	gender?: string;
	dateOfBirth?: string;
	age?: number;
	jobTitle?: string;
	companyName?: string;
	marital?: string;
	countryId?: number;
	country?: Country;
	districtId?: number;
	district?: HkDistrict;
	regionId?: number;
	region?: HkRegion;
	qrCodes?: QRCode[];
	contactProfiles?: (ContactProfile | ContactProfileDto)[];
	data?: ContactData;
	createdAt?: Date;
	updatedAt?: Date;
	deletedAt?: Date | null;
}

export interface MergeContactDto {
	targetContactId: number;
	mergingContactIds: number[];
}

export enum ContactTypeCode {
	volunteer = 'volunteer',
	beneficiary = 'beneficiary',
	member = 'member',
	donor = 'donor',
	participant = 'participant',
	jobParticipant = 'jobParticipant',
	partner = 'partner',
	case = 'case',
}

export function isContactTypeCode(v: unknown): v is ContactTypeCode {
	return ([
		ContactTypeCode.volunteer,
		ContactTypeCode.beneficiary,
		ContactTypeCode.member,
		ContactTypeCode.donor,
		ContactTypeCode.participant,
		ContactTypeCode.jobParticipant,
		ContactTypeCode.partner,
		ContactTypeCode.case,
	] as string[]).includes(v as string);
}

export interface ContactProfile {
	id: number;
	data: FormData;
	contact?: Contact;
	contactId: number;
	profileCode: string;
	paymentPlanId?: string;
	paymentId?: string;
	status: ContactProfileStatusCode;
	formId?: number;
	submitFormId: string; //used for checking payment status
	familyApplicantType?: FamilyApplicantType;
	familyId?: string | null;
	type: ContactTypeCode;
	profileType?: ProfileType;
	profileTypeId?: number;
	profileId: number;
	profileStatuses: ProfileStatus[];
	paymentStatus?: ContactProfilePaymentStatusCode;
	startDate?: Date;
	endDate?: Date;
	reason?: string;
	ownerCenterIds: number[] | null;
	signupCenterIds: number[] | null;

	// case fields
	caseId?: string;
	caseOpenedDate?: string;
	caseClosedDate?: string;
	caseAccessibleUserIds?: string[];
	caseNoOfNotes?: number;

	createdAt: Date;
	updatedAt: Date;
	deletedAt?: Date | null;
}

export interface MembershipApplication {
	profileStatusId: number;
	profileStatusSubmitFormId: string;
	profileStatusPaymentId: string;
	profileTypeName: MultiLangText;
	centerId: number;
	fullNameChi: string;
	firstNameEng: string;
	lastNameEng: string;
	fullNameEng: string;
	contactId: number;
	contactProfileId: string;
	formId: number;
	paymentPlanId: string;
	type: ContactTypeCode;
	profileTypeId: string;
	status: ContactProfileStatusCode;
	signupCenterIds: number[];
	ownerCenterIds: number[];
	familyApplicantType: FamilyApplicantType;
	paymentId: string;
	paymentStatus: ContactProfilePaymentStatusCode;
	createdAt: Date;
	updatedAt: Date;
	qrCodeIds?: string;
	qrCodes?: string;
	isRenewApplication?: boolean;
}

export interface CaseNote {
	id: number;
	contactProfile: ContactProfile;
	noteDate: string;
	location?: string;
	startTime?: string;
	endTime?: string;
	title: string;
	description: string;
	updatedByUserId: string;
	createdAt: Date;
	updatedAt: Date;
}

export enum SubgroupTypeCode {
	volunteer = 'volunteer',
	member = 'member',
	performance = 'performance', //for job/event participants
	experience = 'experience', //for job/event participants
	applications = 'applications', //for job/event participants
}

export interface SubgroupedContactProfiles {
	subgroupKey: string;
	profiles: ContactProfile[];
}

export function isKeyOfContactProfile(
	key: string,
): key is keyof ContactProfile {
	return [
		'id',
		'data',
		'contact',
		'contactId',
		'paymentPlanId',
		'paymentId',
		'status',
		'formId',
		'submitFormId',
		'type',
		'createdAt',
		'paymentStatus',
	].includes(key);
}

export interface ContactProfileDto {
	id?: number;
	data?: FormData;
	contact?: ContactDto;
	contactId?: number;
	paymentPlanId?: string;
	paymentId?: string;
	submitFormId?: string;
	status?: ContactProfileStatusCode;
	formId?: number;
	type?: ContactTypeCode;
	paymentStatus?: ContactProfilePaymentStatusCode;
	startDate?: Date;
	endDate?: Date;
	profileType?: ProfileType;
	profileTypeId?: number;
	reason?: string;
	familyApplicantType?: FamilyApplicantType;
	ownerCenterIds?: number[] | null;
	signupCenterIds?: number[] | null;

	// case fields
	caseId?: string;
	caseOpenedDate?: string | null;
	caseClosedDate?: string | null;
	caseAccessibleUserIds?: string[];
}

export interface ContactProfileCaseNoteDto {
	noteDate: string;
	location?: string;
	startTime?: string;
	endTime?: string;
	title: string;
	description: string;
}

// a subset of ContactProfileDto for application
export interface ContactProfileApplicationDto {
	id?: number;
	status?: ContactProfileStatusCode;
	paymentStatus?: ContactProfilePaymentStatusCode;
	reason?: string;
}

export interface ContactEmailItem {
	email: string;
	type: string;
}

export interface ContactPhoneItem {
	phone: string;
	type: string;
}

export interface ContactAddressItem {
	address: string;
	type: string;
}

export interface ProfileStatus {
	id: number;
	contactProfile?: ContactProfile;
	contactProfileId: number;
	paymentPlanId?: string;
	paymentId?: string;
	paymentStatus?: ContactProfilePaymentStatusCode;
	startDate?: Date;
	endDate?: Date;
	status?: ContactProfileStatusCode;
	submitFormId?: string;
}

//-----------payment-----------
export interface Plan {
	id: string;
	name: MultiLangText;
	currency?: string;
	limitation?: Record<string, any>;
	signupCenterSelectable: boolean;
	centerIds: number[] | null;
	category: string;
	code: string;
	freeOfCharge: boolean;
	published: boolean;
	stripeProductId: string;
	stripePriceId: string;
	feeRules: { [k: string]: any };
	ngoId: number;
	subCategory?: string;
	createdAt: Date;
	updatedAt: Date;
	periodSettings: PeriodSettings;
	paymentMethods?: PlanPaymentMethod[];
	paymentAmounts?: { [k: string]: any };
}

export interface PlanDto {
	id?: string;
	name?: MultiLangText;
	currency?: string;
	limitation?: Record<string, any>;
	signupCenterSelectable: boolean;
	centerIds: number[] | null;
	category?: string;
	code?: string;
	published?: boolean;
	stripeProductId?: string;
	stripePriceId?: string;
	feeRules?: { [k: string]: any };
	ngoId?: number;
	subCategory?: string;
	periodSettings?: PeriodSettings;
	paymentMethods?: PlanPaymentMethod[];
	paymentAmounts?: { [k: string]: any };
}
export enum PeriodType {
	'fixed-period' = 'fixed-period',
	'fiscal-period' = 'fiscal-period',
	perpetual = 'perpetual',
}

export enum DurationUnit {
	month = 'month',
	year = 'year',
}

export interface PeriodSettings {
	type: PeriodType;
	fixedPeriodAmount?: number;
	fixedPeriodUnit?: DurationUnit;
	fiscalPeriodCount?: number;
	fiscalPeriodStartDay?: number;
	fiscalPeriodStartMonth?: number;
}

export interface StripeProduct {
	id: string;
	data: {
		metadata: {
			code: string;
		};
		name: string;
		price: number;
		created: number;
		updated: number;
	};
}

export interface PaymentTypeDto {
	eventId?: string;
	group?: PlanPaymentMethod;
	paymentMethod?: PaymentMethodCode;
	title?: MultiLangText;
	instruction?: MultiLangText;
	isInternal?: boolean;
}

export type PaymentType = 'pay' | 'receipt' | 'refund' | 'waive';

export enum PaymentCategory {
	adhoc = 'adhoc',
	member = 'member',
	volunteer = 'volunteer',
	event = 'event',
	manual = 'manual',
}

export enum PaymentUserCategory {
	member = 'member',
	serviceFee = 'serviceFee',
	sponsorship = 'sponsorship',
	misc = 'misc',
	donation = 'donation',
}

export enum PaymentMethodCode {
	stripe = 'stripe',
	// stripeManual = 'stripe-manual',
	cash = 'cash',
	cheque = 'cheque',
	fps = 'fps',
	atmTransfer = 'atm-transfer',
	bankTransfer = 'bank-transfer',
	payme = 'payme',
	paypal = 'paypal',
	wechatPay = 'wechat-pay',
	alipay = 'alipay',
	octopusCard = 'octopus-card',
	applePay = 'apple-pay',
	googlePay = 'google-pay',
	bankWiring = 'bank-wiring',
	tapAndGo = 'tap-and-go',
	visa = 'visa',
	master = 'master',
	eps = 'eps',
	unionPay = 'union-pay',
	internalTransfer = 'internal-transfer',
	waive = 'waive',
	free = 'free',
}

export enum PlanPaymentMethod {
	payAtTheDoor = 'payAtTheDoor',
	stripe = 'stripe',
	waive = 'waive',
	free = 'free',
	manual = 'manual',
}

export enum EventPaymentMethodGroup {
	payAtTheDoor = 'payAtTheDoor',
	stripe = 'stripe',
}

export interface Transaction {
	id: string;
	issueDate: Date;
	paymentType: PaymentType;
	stripeStatus: string;
	receipt?: Receipt;
	amount: number;
	centerId: number;
	transactionDate: Date;
	transactionNo: string;
	paymentMethod?: PaymentMethodCode;
	fullNameChi?: string;
	firstNameEng?: string;
	lastNameEng?: string;
	status?: string;
	remarks?: string;
	stripeSessionId?: string;
	paymentIntentId?: string;
	stripeRefundId?: string;
	currency?: string;
	createdAt: Date;
	updatedAt: Date;
	payment: Payment;
	bankInBatch: BankInBatch;
	bankInRemarks?: string;
	bankInUpdatedBy?: string;
	bankInUpdatedAt?: Date;
}

export interface BankInBatch {
	id: string;
	ngoId: number;
	centerId: number;
	currency: string;
	payAmount?: number; //in cents
	refundAmount?: number; //in cents
	netAmount?: number; //in cents
	bankInDate: Date;
	updatedBy: string;
	updatedById: string; // admin ID is uuid
	createdAt: Date;
	updatedAt: Date;
}

export interface Payment {
	id: string;
	centerId: number;
	paymentNo: string;
	eventCode?: string;
	contactId?: number;
	category: string;
	isManual: boolean;
	userCategory: string;
	plan: Plan;
	planId: string;
	currency: string;
	remarks?: string;
	amount?: number;
	eventStartDate?: Date;
	eventEndDate?: Date;
	paidAmount: number;
	method?: PaymentMethodCode;
	paymentIntentId?: string;
	transactions: Transaction[];
	status: PaymentStatusCode;
	desc: MultiLangText;
	collaboratorAllCenters: boolean;
	collaboratorCenterIds: number[] | null;
	firstNameEng?: string;
	lastNameEng?: string;
	fullNameChi?: string;
	requisitionNumber?: string;
}

export enum PaymentStatusCode {
	succeeded = 'succeeded',
	waived = 'waived',
	failed = 'failed',
	pending = 'pending',
	refunded = 'refunded',
	partiallyRefunded = 'partiallyRefunded',
	voided = 'voided',
}

export interface TransactionSnapshot {
	paymentType?: PaymentType;
	paymentMethod?: PaymentMethodCode;
	status?: string;
	fullNameChi?: string;
	firstNameEng?: string;
	lastNameEng?: string;
	remarks?: string;
	stripeSessionId?: string;
	paymentIntentId?: string;
	stripeRefundId?: string;
	currency?: string;
	amount: number;
	stripeStatus?: string;
	transactionDate?: Date;
	isVoided: boolean;
}

export interface Receipt {
	id: string;
	centerId: number;
	ngoLogo: string;
	ngoName: MultiLangText;
	ngoLegalEntityName: MultiLangText;
	ngoAddress: {
		address: MultiLangText;
	};
	ngoPhones: {
		general?: { phone: string };
		fax?: { phone: string };
	};
	centerName?: MultiLangText;
	centerAddress?: {
		address: MultiLangText;
	};
	centerPhones?: {
		general?: { phone: string };
		fax?: { phone: string };
	};
	payerName: string;
	payerFullNameChi?: string;
	payerFirstNameEng?: string;
	payerLastNameEng?: string;
	total: number;
	description: string;
	currency: string;
	paymentMethod: PaymentMethodCode;
	remarks: string;
	voidReason?: string;
	receiptNo?: string;
	issueDate?: Date;
	status: 'issued' | 'pending' | 'voided';
	transactionSnapshots: TransactionSnapshot[];
}

export interface CashTransaction {
	id: string;
	issueDate: Date;
	paymentType: PaymentType;
	stripeStatus: string;
}

export interface DataTableExportField {
	name: string;
	key: string;
}

export interface DataTableProps {
	systemHeaders: Header[];
	items?: TableItems;
	totalItems?: number;
	itemKey?: string;
	getItems?: Function;
	allowExport?: boolean;
	onExport?: (opts: TableItemsOptions, fields: DataTableExportField[]) => void;
	props: Props;
	rowNavigation?: Function;
	disableFilter?: boolean;
	exportFilename?: Function;
	loading?: boolean;
	hidePagination?: boolean;
}

export enum TableColumnType {
	string = 'string',
	number = 'number',
	boolean = 'boolean',
	date = 'date',
}

export interface Header {
	text?: string; //TranslateResult
	key?: string; // all fields need this key for saving to preference and display, except the "action" column
	keyOptions?: any;
	value: string;
	align?: string;
	sortable?: boolean;
	nonDraggable?: boolean;
	width?: string | number;
	filterable?: boolean;
	isObject?: boolean;
	langData?: any;
	type?: TableColumnType;
	name?: string;
	parent?: string;
	fixed?: boolean;
	formats?: {
		date?: string;
		time?: string;
	};
}

export type HeaderPreference = Pick<Header, 'key' | 'align'>;

export interface TableItemsOptions {
	url?: string;
	page: number;
	itemsPerPage: number;
	sortBy?: string[];
	sortDesc?: boolean[];
	groupBy: string[];
	groupDesc: boolean[];
	multiSort: boolean;
	mustSort: boolean;
	search: SearchParam;
	filter: number[];
	filterParams: { [f: number]: string };
	isExport?: boolean;
	extraParams?: { [key: string]: string };
}

export interface SearchParam {
	headers: string[];
	text: string;
}

export interface TableItemsResult {
	result: TableItems;
	total: number;
}

export type TableItems = ({ id: string | number } | Record<string, any>)[];

export interface Toolbar {
	title: string;
	button: { text: string; icon: string; action: Function };
}

export type Props = { [key: string]: boolean | string };

export interface FieldGroupConfig {
	name: MultiLangText;
	sortOrder: number;
}

export interface ProfileType {
	id: number;
	ngoId: number;
	contactType: ContactTypeCode;
	code: string;
	name: MultiLangText;
	category: ProfileTypeCategoryCode;
}

export interface ContactDictionary {
	id: number;
	ngoId?: number;
	contactType?: ContactTypeCode;
	fields: DictionaryFieldProperties[];
	profileType?: ProfileType;
	fieldGroup?: FieldGroupConfig;
	ngo: Ngo;
	familyApplicantType?: FamilyApplicantType;
	formOnly: boolean;
}

export type FormTypeCode = string;

export interface AuditLog {
	id: number;
	event: string;
	service: string;
	object: string;
	updatedBy: UserAccount;
	transactedAt: Date;
	createdAt: Date;
	data: any;
	beforeData: any;
}

export interface AuditLogIndex {
	id: number;
	event: string;
	service: string;
	object: string;
	updatedBy: UserAccount;
	transactedAt: Date;
	createdAt: Date;
	dataId: string;
}

export type RelationCode =
	| 'mainApplicant'
	| 'grandparent'
	| 'grandchild'
	| 'parent'
	| 'child'
	| 'sibling'
	| 'spouse'
	| 'guardian'
	| 'ward'
	| 'others';

export interface FamilyRelation {
	id: string;
	contact: Contact;
	contactId: number;
	relatedContact?: Contact; // in case it's deleted, which should not happen though
	relatedContactId: number;
	relation: RelationCode;
	createdAt: Date;
	updatedAt: Date;
	deletedAt: Date;
}

export interface FamilyMember {
	profile: ContactProfile;
	relation: FamilyRelation;
}

export interface FamilyRelationDto {
	contactId?: number;
	relatedContactId?: number;
	relation: RelationCode;
}

export interface FamilyContact extends Contact {
	relation: RelationCode;
}

// export type Rules = {
// 	[key: string]: boolean | string | number | undefined | [number, number];
// };

export enum ProfileTypeCategoryCode {
	general = 'general',
	youth = 'youth',
	family = 'family',
}

export enum QRCodeStatusCode {
	active = 'active',
}

export interface QRCode {
	id: string;
	code: string;
	contactId: number;
	status: QRCodeStatusCode;
	ngoId: number;
}

export interface CenterOption {
	value: Center | null;
	text: string;
	disabled: boolean;
}

export interface RenewMembershipFormData {
	paymentMethod: string;
	remarks: string;
	currency: string;
	amount: number;
	isManualExtend?: boolean;
}

export enum FamilyApplicantType {
	main = 'main',
	dependant = 'dependant',
}

export type UserPermissionRawRule = SubjectRawRule<Action, string, never>;

export interface AccessTokenDto {
	email: string;
	roles: [];
	centerId: number;
	ngoId: number;
	permissions: PackRule<UserPermissionRawRule>[];
	features: string[]; //TODO: type it?
}

export interface LastModifiedInfo {
	userId?: string;
}

/**
 * shared by job and event
 */
export interface ItemWithLocation {
	isVirtual?: boolean;
	regionCode?: string;
	districtCode?: string;
	subDistrictCode?: string;
	addressLine?: MultiLangText | null;
	mapLatLong?: string | null;
}

export interface ItemWithOwnerCenter {
	ownerCenterId: number;
	collaboratorAllCenters: boolean;
	collaboratorCenterIds: number[] | null;
	partners?: PartnerInfo[] | null;
}

export interface PartnerInfo {
	partner: PartnerDetail;
	data: PartnerData;
}

export interface PartnerDetail {
	partnerId: string;
	orgId: string;
	isExternal: boolean;
	centerId?: number | null;
}

export interface PartnerData {
	orgName: MultiLangText;
	unitName?: MultiLangText | null;
}

export enum TimeslotRecurrenceType {
	daily = 'daily',
	weekly = 'weekly',
	monthly = 'monthly',
}

export const recurrenceTypeOptions = [
	TimeslotRecurrenceType.daily,
	TimeslotRecurrenceType.weekly,
	TimeslotRecurrenceType.monthly,
].map(code => ({
	text: `pages.events.schedule.recurrence.type.${code}`,
	value: code,
}));

export enum Weekday {
	sunday = 'sunday',
	monday = 'monday',
	tuesday = 'tuesday',
	wednesday = 'wednesday',
	thursday = 'thursday',
	friday = 'friday',
	saturday = 'saturday',
}

export const recurrenceWeekdayCodes = [
	Weekday.sunday,
	Weekday.monday,
	Weekday.tuesday,
	Weekday.wednesday,
	Weekday.thursday,
	Weekday.friday,
	Weekday.saturday,
];

export const recurrenceWeekdayOptions = recurrenceWeekdayCodes.map(code => ({
	text: `pages.events.schedule.recurrence.${code}`,
	value: code,
}));

export enum TimeslotRecurrenceMonthlyMode {
	dayOfMonth = 'dayOfMonth',
	dayOfWeek = 'dayOfWeek',
}

export enum RepeatWeekOptions {
	first = 'first',
	second = 'second',
	third = 'third',
	fourth = 'fourth',
	fifth = 'fifth',
	last = 'last',
}

export const recurrenceRepeatWeekOptions = [
	RepeatWeekOptions.first,
	RepeatWeekOptions.second,
	RepeatWeekOptions.third,
	RepeatWeekOptions.fourth,
	RepeatWeekOptions.fifth,
	RepeatWeekOptions.last,
].map(code => ({
	text: `pages.events.schedule.recurrence.monthly.repeatWeekOptions.${code}`,
	value: code,
}));

export enum RepeatDayOfWeekOptions {
	sunday = 'sunday',
	monday = 'monday',
	tuesday = 'tuesday',
	wednesday = 'wednesday',
	thursday = 'thursday',
	friday = 'friday',
	saturday = 'saturday',
	day = 'day',
	weekday = 'weekday',
	weekend = 'weekend',
}

export const recurrenceRepeatDayOfWeekOptions = [
	RepeatDayOfWeekOptions.sunday,
	RepeatDayOfWeekOptions.monday,
	RepeatDayOfWeekOptions.tuesday,
	RepeatDayOfWeekOptions.wednesday,
	RepeatDayOfWeekOptions.thursday,
	RepeatDayOfWeekOptions.friday,
	RepeatDayOfWeekOptions.saturday,
	RepeatDayOfWeekOptions.day,
	RepeatDayOfWeekOptions.weekday,
	RepeatDayOfWeekOptions.weekend,
].map(code => ({
	text: `pages.events.schedule.recurrence.monthly.repeatDayOfWeek.${code}`,
	value: code,
}));

// the user table in volunteer-service
export interface Volunteer {
	chineseName: string;
	firstName: string;
	lastName: string;
	userCode: string;
	redFlag: boolean;
}
